import React, { forwardRef, KeyboardEventHandler, RefObject } from 'react';
import classNames from 'classnames';

import { Comp, Number, Text, useId, useTheme } from '@redsift/design-system';
import { Tooltip } from '@redsift/popovers';

import { DataPointProps } from './types';
import { StyledDataPoint } from './styles';
import { TooltipVariant } from '../../types';

export const DataPoint: Comp<DataPointProps<any>, SVGGElement> = forwardRef((props, ref) => {
  const {
    anchorProps: { as, href, ...anchorProps } = {},
    children,
    className,
    data,
    id: propsId,
    index,
    isSelected: propsIsSelected,
    labelDecorator,
    onClick,
    role,
    tooltipDecorator,
    tooltipVariant,
    theme: propsTheme,
    tooltipProps,
    dataset,
    groupedData,
    ...forwardedProps
  } = props;

  const [_id] = useId();
  const id = propsId ?? _id;

  const theme = useTheme(propsTheme);

  const isTooltipCustom = tooltipVariant === TooltipVariant.custom && tooltipDecorator;
  const text = labelDecorator
    ? labelDecorator(data, { index, isSelected: propsIsSelected, color: props.color })
    : data.data.key;
  const hasText = text && tooltipVariant !== TooltipVariant.none;
  const tooltipValue =
    tooltipVariant === TooltipVariant.value
      ? data.data.value
      : tooltipVariant === TooltipVariant.percent && data.data.percent
      ? data.data.percent
      : undefined;
  const hasValue = tooltipValue && [TooltipVariant.value, TooltipVariant.percent].includes(tooltipVariant!);
  const showTooltip = tooltipVariant !== TooltipVariant.none && (hasText || hasValue);

  const isEmpty = data.data.value === 0;

  const isSelectable = role === 'option';
  const isSelected = isSelectable && propsIsSelected === true;
  const isDeselected = isSelectable && propsIsSelected === false;

  const onKeyDown: KeyboardEventHandler<SVGElement> = (event) => {
    if (onClick) {
      event.stopPropagation();

      if (event.code === 'Enter' || event.code === 'Space') {
        event.preventDefault();
        onClick(data, dataset);
      }
    }
  };

  const DataPointComponent = (
    <StyledDataPoint
      tabIndex={onClick ? 0 : undefined}
      aria-label={
        showTooltip && !isEmpty ? (labelDecorator ? text : `${data.data.key}, ${data.data.value}`) : undefined
      }
      aria-labelledby={!showTooltip && !isEmpty ? `${id}-title` : undefined}
      aria-selected={isSelected ? true : isDeselected ? false : undefined}
      onClick={onClick ? () => onClick(data, dataset) : undefined}
      onKeyDown={onClick ? (event: React.KeyboardEvent<SVGElement>) => onKeyDown(event) : undefined}
      role={role ? role : onClick ? 'button' : href ? undefined : 'img'}
      {...forwardedProps}
      className={classNames(className, `_${index}`, {
        selected: isSelected,
        deselected: isDeselected,
      })}
      ref={ref as RefObject<SVGGElement>}
      id={id}
      $clickable={Boolean(onClick || href)}
    >
      {children}
      {!showTooltip && !isEmpty ? <title id={`${id}-title`}>{`${text}: ${data.data.value}`}</title> : null}
    </StyledDataPoint>
  );

  const MaybeAnchor =
    href !== undefined
      ? React.createElement(as || 'a', { href, ...anchorProps }, DataPointComponent)
      : DataPointComponent;

  if (showTooltip) {
    return (
      <Tooltip placement="right" theme={theme} delay={50} {...tooltipProps}>
        <Tooltip.Trigger>{MaybeAnchor}</Tooltip.Trigger>
        <Tooltip.Content>
          {isTooltipCustom ? (
            tooltipDecorator(data, { index, isSelected: propsIsSelected, color: props.color }, dataset, groupedData)
          ) : (
            <Text variant="caption">
              {text}
              {hasText && hasValue ? ' - ' : null}
              {hasValue ? (
                <Number
                  value={tooltipValue!}
                  type={tooltipVariant === TooltipVariant.percent ? 'percent' : 'decimal'}
                  variant="caption"
                  maximumFractionDigits={2}
                />
              ) : null}
            </Text>
          )}
        </Tooltip.Content>
      </Tooltip>
    );
  }

  return MaybeAnchor;
});
